import * as _ from 'lodash';
import hash from 'object-hash';
import { Commit, Dispatch } from 'vuex';
import { updateTableSchemaAction } from '@/main/store/actionsFunctions';
import { listStoreBuilder } from '@/main/store/pages/list/builder';
import { formatDateTime } from '@/main/utils/dateFormatter';
import { END_LOADING, START_LOADING, TABLE_SCHEMA } from '@/main/store/listEditTypes';
import { ListStoreBase } from '@/main/store/@types/list.d';

import { NAME, CRUMB_LABEL_KEY, TAB_NAMES, ZPRAVA_DRUH } from '../constants';
import { apiProvider } from '../api';
import { listActions, listMutations } from './types';

const zpravaDefault = {
	ZaznamId: null as number | null,
	OdesPracId: null as number | null,
	OdesPracIdText: null as string | null,
	Hlavicka: null as string | null,
	MojePoznamka: null as string | null,
	ZpracovanoPrpZkt: true,
	Text: null as string | null,
	VznikDat: null as string | null,
	PrijemceIds: [
		null as number | null,
	],
	DmsUserMessageId: null as number | null,
	DropDownList: {
		PrijemceIds: [],
	},
};

const getOdpovedetText = ({ OdesPracIdText, VznikDat, Text }: typeof zpravaDefault) => `
____________________________________________________________________________________________________			
Od:                ${OdesPracIdText}
Dne:               ${formatDateTime(VznikDat)}

${Text}`;

export const store = listStoreBuilder({
	name: NAME,
	apiProvider,
	crumbLabel: CRUMB_LABEL_KEY,
	dropdownList: { VyrobekvozOzn: [] },
	filter: {
		zpravyDruhEnum: ZPRAVA_DRUH.ZPRAVY as ZPRAVA_DRUH | null,
		vyrizene: false as boolean | null,
	},
	tableSetting: {},
	rowDetail: null,
	localState: {
		loggedUserId: null as number | null,
		visibleDetail: false,
		zprava: zpravaDefault,
		hash: null as string | null,
		isNovaZprava: null as boolean | null,
		tabsBadges: {
			[TAB_NAMES.ZPRAVY]: null,
			[TAB_NAMES.ODESLANE]: null,
			[TAB_NAMES.POZNAMKY]: null,
			[TAB_NAMES.SMS]: null,
			[TAB_NAMES.SYSTEMOVE_ZPRAVY]: null,
			[TAB_NAMES.SYSTEMOVA_UPOZORNENI]: null,
		},
	},
	extraActions: {
		async [TABLE_SCHEMA]({ state, commit, dispatch }: { state: ListStoreBase, commit: Commit, dispatch: Dispatch }) {
			return updateTableSchemaAction(state, commit, dispatch,
				apiProvider.tableSchema({ cancelTokenKey: TABLE_SCHEMA, commit, filter: state.filter }), 'table');
		},
		[listActions.GET_ZPRAVY_COUNT]: async ({ commit }) => {
			try {
				commit(START_LOADING);
				const res = await apiProvider.getZpravyCount();
				commit(listMutations.SET_ZPRAVY_COUNT, res);
			} finally {
				commit(END_LOADING);
			}
		},
		[listActions.NOVA_ZPRAVA]: async ({ commit }) => {
			try {
				commit(START_LOADING);
				const res = await apiProvider.default();
				commit(listMutations.NASTAV_DETAIL, res);
				commit(listMutations.SET_IS_NOVA_ZPRAVA, true);
			} finally {
				commit(END_LOADING);
			}
		},
		[listActions.NACTI_ZPRAVU]: async ({ commit }: any, { zaznamId, dmsUserMessageId, druh }:
		{ zaznamId: number, dmsUserMessageId: number, druh: ZPRAVA_DRUH }) => {
			try {
				commit(START_LOADING);
				let res;
				if (druh === ZPRAVA_DRUH.SYSTEMOVA_UPOZORNENI) {
					res = await apiProvider.getSystemoveUpozorneni(dmsUserMessageId);
				} else if (zaznamId) {
					res = await apiProvider.entity(zaznamId);
				} else {
					res = await apiProvider.getZpravaUserMessage(dmsUserMessageId);
				}
				commit(listMutations.NASTAV_DETAIL, res);
				commit(listMutations.SET_IS_NOVA_ZPRAVA, false);
			} finally {
				commit(END_LOADING);
			}
		},
		[listActions.ODESLAT_ZPRAVU]: async ({ commit, state }: any) => {
			try {
				commit(START_LOADING);
				await apiProvider.createEntity(state.localState.zprava);
				commit(listMutations.ZAVRIT_DETAIL);
			} finally {
				commit(END_LOADING);
			}
		},
		[listActions.ULOZIT_ZPRAVU]: async ({ commit, state }: any) => {
			try {
				commit(START_LOADING);
				await apiProvider.updateEntity(state.localState.zprava.ZaznamId, state.localState.zprava);
			} finally {
				commit(END_LOADING);
			}
		},

		[listActions.VYRIDIT_AKTIVOVAT_ZPRAVU]: async ({ state, dispatch, commit }, vyridit) => {
			try {
				commit(START_LOADING);
				await apiProvider.vyriditAktivovatZpravu(state.localState.zprava.ZaznamId!, vyridit);
				await dispatch(listMutations.SET_VYRIDIT_AKTIVOVAT_ZPRAVU, vyridit);
			} finally {
				commit(END_LOADING);
			}
		},
		[listActions.DO_POZNAMEK]: async ({ state, dispatch, commit }) => {
			try {
				commit(START_LOADING);
				await apiProvider.doPoznamek(state.localState.zprava.DmsUserMessageId!);
				await dispatch(listActions.GET_ZPRAVY_COUNT);
			} finally {
				commit(END_LOADING);
			}
		},
	},
	extraMutations: {
		[listMutations.SET_ZPRAVY_COUNT]: (state, { data }) => {
			state.localState.tabsBadges = {
				[TAB_NAMES.ZPRAVY]: data.Local,
				[TAB_NAMES.ODESLANE]: data.Odeslane,
				[TAB_NAMES.POZNAMKY]: data.Poznamky,
				[TAB_NAMES.SMS]: data.Sms,
				[TAB_NAMES.SYSTEMOVE_ZPRAVY]: data.System,
				[TAB_NAMES.SYSTEMOVA_UPOZORNENI]: null, // neni ani v DMS 2
			};
		},
		[listMutations.ODPOVEDET]: (state) => {
			state.localState.zprava = {
				...zpravaDefault,
				DmsUserMessageId: state.localState.zprava.DmsUserMessageId,
				PrijemceIds: [state.localState.zprava.OdesPracId],
				Hlavicka: `Re: ${state.localState.zprava.Hlavicka}`,
				Text: getOdpovedetText(state.localState.zprava),
				DropDownList: state.localState.zprava.DropDownList,
			};
			state.localState.isNovaZprava = true;
		},
		[listMutations.ODPOVEDET_VSEM]: (state) => {
			state.localState.zprava = {
				...zpravaDefault,
				DmsUserMessageId: state.localState.zprava.DmsUserMessageId,
				PrijemceIds: _.filter([state.localState.zprava.OdesPracId, ...state.localState.zprava.PrijemceIds], (id) => id !== state.localState.loggedUserId),
				Hlavicka: `Re: ${state.localState.zprava.Hlavicka}`,
				Text: getOdpovedetText(state.localState.zprava),
				DropDownList: state.localState.zprava.DropDownList,
			};
			state.localState.isNovaZprava = true;
		},

		[listMutations.PREDAT_DAL]: (state) => {
			state.localState.zprava = {
				...zpravaDefault,
				DmsUserMessageId: state.localState.zprava.DmsUserMessageId,
				PrijemceIds: [],
				Hlavicka: `Fw: ${state.localState.zprava.Hlavicka}`,
				Text: getOdpovedetText(state.localState.zprava),
				DropDownList: state.localState.zprava.DropDownList,
			};
			state.localState.isNovaZprava = true;
		},
		[listMutations.NASTAV_DETAIL]: (state, { data }) => {
			state.localState.zprava = data;
			state.localState.visibleDetail = true;
			state.localState.hash = hash(state.localState.zprava);
		},
		[listMutations.ZAVRIT_DETAIL]: (state) => {
			state.localState.zprava = zpravaDefault;
			state.localState.visibleDetail = false;
			state.localState.isNovaZprava = false;
		},
		[listMutations.SET_IS_NOVA_ZPRAVA]: (state, isNovaZprava) => {
			state.localState.isNovaZprava = isNovaZprava;
		},
		[listMutations.SET_VYRIDIT_AKTIVOVAT_ZPRAVU]: (state, vyridit) => {
			state.localState.zprava.ZpracovanoPrpZkt = vyridit;
		},
	},
	extraGetters: {},
	props: {
		filter: {},
		mode: null,
		afterRowSelection: () => {},
	},
});
